import {
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	IconButton,
	Spinner,
	useDisclosure,
} from 'src/components/ui';
import { useState } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import {
	MdContentCopy,
	MdDelete,
	MdEdit,
	MdPause,
} from 'react-icons/md';
import { toastError, toastSuccess } from 'src/services/toast';
import DeactivateCampaignConfirmModal from './DeactivateCampaignConfirmModal';

interface CampaignOptionsMenuProps {
	onEdit: () => void;
	onDuplicate: () => Promise<void>;
	onDelete: () => Promise<void>;
	onDeactivate?: () => Promise<void>;
	status?: 'draft' | 'published' | 'archived' | 'finalized' | 'publishing';
	campaignTitle?: string;
}

const CampaignOptionsMenu = ({
	onEdit,
	onDuplicate,
	onDelete,
	onDeactivate,
	status,
	campaignTitle,
}: CampaignOptionsMenuProps) => {
	const [isDeleting, setIsDeleting] = useState(false);
	const [isDuplicating, setIsDuplicating] = useState(false);
	const [isDeactivating, setIsDeactivating] = useState(false);
	const {
		isOpen: isDeactivateModalOpen,
		onOpen: onOpenDeactivateModal,
		onClose: onCloseDeactivateModal,
	} = useDisclosure();

	const handleDeactivateConfirm = async () => {
		if (!onDeactivate) return;
		setIsDeactivating(true);
		try {
			await onDeactivate();
			toastSuccess('The campaign has been deactivated and ads have been paused.');
		} catch (error) {
			toastError('There was an error deactivating the campaign.');
		} finally {
			setIsDeactivating(false);
		}
	};

	const handleDeleteClick = async () => {
		setIsDeleting(true);
		try {
			await onDelete();
			toastSuccess('The campaign has been successfully deleted.');
		} catch (error) {
			toastError('There was an error deleting the campaign.');
		} finally {
			setIsDeleting(false);
		}
	};

	const handleDuplicateClick = async () => {
		setIsDuplicating(true);
		try {
			await onDuplicate();
			toastSuccess('The campaign has been successfully duplicated.');
		} finally {
			setIsDuplicating(false);
			toastError('There was an error duplicating the campaign.');
		}
	};

	return (
		<Menu closeOnSelect={true}>
			<MenuButton
				as={IconButton}
				aria-label="Options"
				icon={<BsThreeDotsVertical />}
				variant="ghost"
				size="sm"
			/>
			<MenuList>
				<MenuItem onClick={onEdit} icon={<MdEdit size={15} />}>
					Edit
				</MenuItem>
				<MenuItem
					onClick={handleDuplicateClick}
					isDisabled={isDuplicating}
					icon={<MdContentCopy size={15} />}
				>
					{isDuplicating ? (
						<Spinner size="sm" color="orange.500" />
					) : (
						'Duplicate'
					)}
				</MenuItem>
			{status === 'published' && onDeactivate && (
				<MenuItem
					onClick={onOpenDeactivateModal}
					icon={<MdPause size={15} />}
				>
					Deactivate
				</MenuItem>
			)}
				<MenuItem
					onClick={handleDeleteClick}
					isDisabled={isDeleting}
					icon={<MdDelete size={15} />}
				>
					{isDeleting ? <Spinner size="sm" color="red.500" /> : 'Delete'}
				</MenuItem>
			</MenuList>

			<DeactivateCampaignConfirmModal
				isOpen={isDeactivateModalOpen}
				onClose={onCloseDeactivateModal}
				onConfirm={handleDeactivateConfirm}
				isLoading={isDeactivating}
				campaignTitle={campaignTitle}
			/>
		</Menu>
	);
};

export default CampaignOptionsMenu;
